import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation} from 'react-router-dom';
import { toast } from 'react-toastify';
import { clone, equals, findIndex, isEmpty, isNil, length, move, propEq, remove, slice } from 'ramda';
import Button from '@components/@core/AnimatedButton';
import Dropdown from '@components/@core/Dropdown';
import EditDropdown from '@components/EditDropdown';
import Filter from '@components/@core/Filter';
import Icon from '@components/@core/Icon';
import Input from '@components/@core/Input';
import Thumbnail from '@components/@core/Thumbnail';
import Settings from '@components/@core/Settings';
import AppActions from '~/actions/app';
import DevelopActions from '~/actions/develop';
import { LibraryCardEditMenu } from '~/services/config';
import Module from './Module';

const ADDMODULES = [
    { id: 0, value: 'Add Existing', name: 'old' },
    { id: 1, value: 'Create New', name: 'new' },
];

const Edit = ({ data = {}, onCardMenu, onChildMenu }) => {
    // Destructured Props
    const { 
        // i* = initial --> Will later become intial value of a state variable
        // data.* || data.data.*  --> Handle inconsistent implementations
        iName = data.data?.en?.name || '', 
        iNameEs = data.data?.es?.name || '', 
        iDescription = data.description || data.data?.en?.description || '', 
        iDescriptionEs = data.data?.es?.description || '',
        iThumbnail = data.thumb_url || data.data?.thumb_url || '', 
        iChildren = data.children || data.data?.children || [], 
        iObjectives = data.objectives || data.data?.objectives || [], 
        iDepartment = data.department || data.data?.department || '',
        iCompetency = data.competency || data.data?.competency || '',
        iCategory = data.category || data.data?.category || '',
        iAuthor = data.author_id || '',
        timeLock = data.time_lock || data.data?.time_lock || false,
        chronologicalLock = data.chronological_lock || data.data?.chronological_lock || false,
        published = data.published || data.data?.published || false,
    } = data;

    // Setup Hooks
    const dispatch = useDispatch();
    const location = useLocation();

    // Redux Dispatched Functions
    const modalRequest = (payload) => dispatch(AppActions.modalRequest(payload));
    const saveTemplate = (payload, files, target, after, route, callback) => 
        dispatch(DevelopActions.librarytemplatesaveRequest(payload, files, target, after, route, callback));

    // Redux Managed State
    const app = useSelector((state) => state.app);
    const {departments, competencies, categories, authors, modalData, role = app.app_role_id} = app;

    // Local State Variables
    const [nameEn, setNameEn] = useState(iName);
    const [nameEs, setNameEs] = useState(iNameEs);
    const [descriptionEn, setDescriptionEn] = useState(iDescription);
    const [descriptionEs, setDescriptionEs] = useState(iDescriptionEs);
    const [thumbnail, setThumbnail] = useState(iThumbnail);
    const [children, setChildren] = useState(iChildren);
    const [objectives, setObjectives] = useState(iObjectives);
    const [selected, setSelected] = useState({
        author: [iAuthor],
        department: iDepartment,
        competency: iCompetency,
        category: iCategory,
    });

    // Local Constants
    const page = 1;
    const per = 25;
    const from = (page - 1) * per;
    const to = page * per;
    const modules = slice(from, to, children);

    const settings = [
        { name: 'Author', id: [iAuthor], options: authors, select: 'author' },
        { name: 'Departments', id: iDepartment, options: departments, select: 'department' },
        { name: 'Competencies', id: iCompetency, options: competencies, select: 'competency' },
        { name: 'Categories', id: iCategory, options: categories, select: 'category' },
    ];
   
    const disabled =
        isEmpty(nameEn) 
        || isNil(descriptionEn) 
        || isNil(children) 
        || isEmpty(children) 
        || (   
            equals(nameEn, iName) 
            && equals(descriptionEn, iDescription)
            && equals(selected.author, [iAuthor.id])
            && equals(selected.department, iDepartment)
            && equals(selected.competency, iCompetency)
            && equals(selected.category, iCategory)
            && equals(objectives, iObjectives)
            && equals(children, iChildren)
            && equals(children.length, iChildren.length)
            && equals(thumbnail, iThumbnail)
            && equals(chronologicalLock, data.data.chronological_lock || false)
            && equals(timeLock, data.data.time_lock || false)
        );

    // Event Handlers
    const handleObjectiveChange = (index) => (e) => {
        const _objectives = clone(objectives);
        _objectives[index] = e;
        setObjectives(_objectives);
    };

    const handleSelectTags = (key) => (tags) => {
        const _selected = clone(selected);
        _selected[key] = tags;
        setSelected(_selected);
    };

    const handleSubmit = (published) => () => {
        const extractHTMLContent = (string) => {
            const span = document.createElement('span');
            span.innerHTML = string;
            return span.textContent || span.innerText;
        };

        const validInput = nameEn !== '' && extractHTMLContent(descriptionEn) !== '' && selected.author !== '';

        if (!validInput) {
            toast.error('Please enter Title, Description and Author properly to publish a draft.', {
                position: toast.POSITION.TOP_RIGHT,
                pauseOnFocusLoss: false,
                hideProgressBar: true,
            });
            return
        } else {
            const _objectives = objectives.filter((e) => !!e);
            const modules = children.map((item, key) => ({
                blocked_by: key === 0 ? null : children[key - 1].id,
                card_template_id: item.id,
                card_type_id: item.card_type_id,
                delay_days: item.data.delay_days,
            }));

            const payload = {
                template: {
                    id: data.id,
                    access_type: 1,
                    author_id: selected.author[0],
                    card_type_id: 1,
                    children,
                    published,
                    data: {
                        ...data.data,
                        en: { name: nameEn, description: descriptionEn },
                        es: { name: nameEs, description: descriptionEs },
                        category: selected.category,
                        competency: selected.competency,
                        department: selected.department,
                        objectives: _objectives,
                        thumb_url: thumbnail || '',
                        modules,
                        module_count: children?.length || 0,
                        chronological_lock: chronologicalLock,
                        time_lock: timeLock,
                    },
                },
            };

            const search = location.state?.filter || '';
            const page = location.state?.page || 1;
            const after = {
                type: 'GETLIBRARYTEMPLATESSEARCH_REQUEST',
                target: 'courses',
                payload: { page, perPage: 25, search },
            };
            console.log({payload, thumbnail: (thumbnail === iThumbnail ? null : [thumbnail]), target: 'courses', after, route: '/library/training?active=courses'});
            saveTemplate(payload, thumbnail === iThumbnail ? null : [thumbnail], 'courses', after, '/library/training?active=courses');
        }
    };

    const handleChildMenu = (event, item) => {
        const index = findIndex(propEq('id', item.id), children);
        if (index > -1) {
            let _children = clone(children);
            if (event === 'move up') {
                if (index === 0) return;
                _children = move(index, index - 1, _children);
            } else if (event === 'move down') {
                if (_children?.length === index + 1) return;
                _children = move(index, index + 1, _children);
            } else if (event === 'remove') {
                _children = remove(index, 1, _children);
            } else {
                onChildMenu(event, item);
            }
            setChildren(_children);
        }
    };

    const handleToggleModal = (e) => {
        if (e[0].name === 'old') {
            modalRequest({
                type: 'Attach Library',
                data: { before: { modules: [], show: ['modules'], selected: children } },
                callBack: { onAttach: (e) => setChildren(e.templates) },
            });
        } else {
            modalRequest({
                type: 'Add New Module',
                data: { after: { modules: children } },
                callBack: handleAddNewModule,
            });
        }
    };

    const handleAddNewModule = (e) => {
        const _children = clone(children);
        _children.push(e.template);
        setChildren(_children);
    };

    const handleSelectMenu = () => (event, module) => {
        const quotaLen = length(children);
        let modules = clone(children);
        const indx = findIndex(propEq('id', module.id), modules);
        if (indx < 0) return;

        switch (event) {
            case 'move up':
                if (indx !== 0) modules = move(indx, indx - 1, modules);
                break;
            case 'move down':
                if (quotaLen !== indx + 1) modules = move(indx, indx + 1, modules);
                break;
            case 'remove':
                modules = remove(indx, 1, modules);
                break;
        }
        setChildren(modules);
    };

    // Unused in Legacy Version , maybe for future use??
    //const handleObjectiveAdd = () => {
    //    const _objectives = clone(objectives);
    //    _objectives.push('');
    //    setObjectives(_objectives);
    //};

    //const handleObjectiveDelete = (index) => () => {
    //    const _objectives = clone(objectives);
    //    _objectives.splice(index, 1);
    //    setObjectives(_objectives);
    //};

    useEffect(() => {
        if (modalData?.after?.modules) {
            setChildren(modalData.after.modules);
        }
    }, [modalData]);

    return (
        <>
            <Filter back="all courses" />
            <div className="library-course-card border-5" data-cy="editCourseForm">
                <div className="detail-content">
                    <div className="d-h-between mb-3">
                        <span className="dsl-b22 bold" data-cy="editTitle">
                            Course
                        </span>
                        <EditDropdown options={LibraryCardEditMenu[role]} onChange={onCardMenu} />
                    </div>
                    <div className="d-flex jc-between ssm-column">
                        <div className="ssm-100 w-50 w-sm-100 mb-3">
                            <div className="pl-1 mb-3">
                                <Input className="course-input mb-3" title="Name" dataCy="title" value={nameEn} maxLength={50} placeholder="Type here..." onChange={(e) => setNameEn(e)} />
                            </div>
                            <div className="pl-1 mb-3">
                                <Input
                                    className="course-text"
                                    title="Description"
                                    dataCy="description"
                                    placeholder="Type here..."
                                    as="textarea"
                                    rows="3"
                                    maxLength={150}
                                    value={descriptionEn}
                                    onChange={(e) => setDescriptionEn(e)}
                                />
                            </div>
                            <div className="pl-1 mb-3">
                                <Input className="course-input mb-3" title="Name (Spanish)" dataCy="title-es" value={nameEs} maxLength={50} placeholder="Type here..." onChange={(e) => setNameEs(e)} />
                            </div>
                            <div className="pl-1 mb-3">
                                <Input
                                    className="course-text"
                                    title="Description (Spanish)"
                                    dataCy="description-es"
                                    placeholder="Type here..."
                                    as="textarea"
                                    rows="3"
                                    maxLength={150}
                                    value={descriptionEs}
                                    onChange={(e) => setDescriptionEs(e)}
                                />
                            </div>
                            <div className="d-flex ai-start ps-1">
                                <p className="career-text core-input-label dsl-b12 pt-2 m-0">Learning Objectives</p>
                                <div className="d-flex flex-column w-100">
                                    {objectives.map((item, index) => (
                                        <Input
                                            className="course-input mb-2 d-flex-1"
                                            value={objectives[index]}
                                            placeholder="Type here..."
                                            direction="vertical"
                                            onChange={handleObjectiveChange(index)}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="ssm-100 w-40 w-sm-100">
                            <Thumbnail type="upload" isDisablePicker source={thumbnail} size="medium" label="Upload image" onDrop={(e) => setThumbnail(e)} onDelete={() => setThumbnail(null)} />
                        </div>
                    </div>
                </div>
            </div>
            <div className="library-course-card mt-2 border-5">
                <div className="modules-content pb-0">
                    <div className="d-flex">
                        <p className="dsl-b22 bold">Modules Attached</p>
                    </div>
                    <Module data={modules} role={role} onMenu={handleChildMenu} onSelect={handleSelectMenu()} edit />
                    <div className="d-h-start justify-content-end my-3 my-sm-0 pb-2 pt-4">
                        <Button className="ml-auto" type="link">
                            <Icon name="fas fa-plus" size={14} color="#376caf" />
                            <Dropdown
                                className="pl-1"
                                align="right"
                                width="fit-content"
                                caret="none"
                                returnBy="data"
                                placeholder="Attach Module"
                                selectable={false}
                                data={ADDMODULES}
                                onChange={handleToggleModal}
                            />
                        </Button>
                    </div>
                </div>
            </div>
            <Settings isEditable expanded values={settings} handleSelect={handleSelectTags} />
            <div className="mt-3 d-flex jc-end">
                <Button className="mr-3" name="Draft" disabled={published ? false : disabled} type="low" onClick={handleSubmit(0)} />
                <Button name="Publish" disabled={published ? disabled : false} onClick={handleSubmit(1)} />
            </div>
        </>
    );
};

export default Edit;
